import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";
import { FormRecordProvider } from "../../../../common/controls/forms/FormRecordContext";
import FormExtras from "../../../../common/components/formExtras";
import { PublisherCreateStore } from "../../store";
import { FirstPublisherBanner } from "../common/FirstPublisherBanner";
import { runInAction } from "mobx";
import { PersonType } from "../../../../api/graphql";
import { InputReference } from "../../../common/controls";
import { InputString } from "../../../common/controls";
import { MaritalStatus } from "../../../../api/graphql";
import { CreateEntityDialog } from "../../../common/controls";
import { CountryCreate } from "../../../country/components/create/CountryCreate";
import { CountryCreateStore } from "../../../country/store";
import { InputStringMask } from "../../../common/controls";
import { InputPhone } from "../../../common/controls";
import { EmailVerificationIndicator } from "../../../common/controls/EmailVerificationIndicator";
import { FormControl } from "@mui/material";
import { ProvinceStateCreate } from "../../../provinceState/components/create/ProvinceStateCreate";
import { ProvinceStateCreateStore } from "../../../provinceState/store";
import { PublisherCaeIpiCustomField } from "../custom/PublisherCaeIpiCustomField";
import { ItemsCRUDCompact } from "../../../common/controls";
import { CrudRow } from "../../../common/types";
import { GridChangeSet } from "../../../common/types";
import { PublisherRepresentativeCreate } from "../../../publisherRepresentative/components/create/PublisherRepresentativeCreate";
import { PublisherRepresentativeCreateStore } from "../../../publisherRepresentative/store";
import { GridColumn } from "../../../common/types";
import { PublisherCollectingSocietyCreate } from "../../../publisherCollectingSociety/components/create/PublisherCollectingSocietyCreate";
import { PublisherCollectingSocietyCreateStore } from "../../../publisherCollectingSociety/store";
import { PersonaFisicaNameFields } from "../common/PersonaFisicaNameFields";

export type PublisherCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  personTypeError?: string;
  personTypeOptions: { id: string; displayId: string }[];
  personType: PersonType;
  onPersonTypeChange?: (value: PersonType) => void;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  name: string;
  nameError?: string;
  maritalStatusError?: string;
  maritalStatusOptions: { id: string; displayId: string }[];
  maritalStatus: MaritalStatus | "";
  onMaritalStatusChange?: (value: MaritalStatus | "") => void;
  nationalitySelectedValue?: { displayId: string; id: string } | null;
  onNationalitySelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onNationalityChange?: (value: string) => void;
  nationalityOptions?: { displayId: string; id: string }[];
  nationalityError?: string;
  pseudonymSelectedValue?: { displayId: string; id: string } | null;
  onPseudonymChange?: (value: string) => void;
  pseudonym: string;
  pseudonymError?: string;
  taxIdSelectedValue?: { displayId: string; id: string } | null;
  onTaxIdChange?: (value: string) => void;
  taxId: string;
  taxIdError?: string;
  phoneSelectedValue?: { displayId: string; id: string } | null;
  onPhoneChange?: (value: string) => void;
  phone: string;
  phoneError?: string;
  emailSelectedValue?: { displayId: string; id: string } | null;
  onEmailChange?: (value: string) => void;
  email: string;
  emailError?: string;
  streetAndNumberSelectedValue?: { displayId: string; id: string } | null;
  onStreetAndNumberChange?: (value: string) => void;
  streetAndNumber: string;
  streetAndNumberError?: string;
  floorSelectedValue?: { displayId: string; id: string } | null;
  onFloorChange?: (value: string) => void;
  floor: string;
  floorError?: string;
  suiteSelectedValue?: { displayId: string; id: string } | null;
  onSuiteChange?: (value: string) => void;
  suite: string;
  suiteError?: string;
  postalCodeSelectedValue?: { displayId: string; id: string } | null;
  onPostalCodeChange?: (value: string) => void;
  postalCode: string;
  postalCodeError?: string;
  countrySelectedValue?: { displayId: string; id: string } | null;
  onCountrySelected?: (value: { displayId: string; id: string } | null) => void;
  onCountryChange?: (value: string) => void;
  countryOptions?: { displayId: string; id: string }[];
  countryError?: string;
  provinceSelectedValue?: { displayId: string; id: string } | null;
  onProvinceSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onProvinceChange?: (value: string) => void;
  provinceOptions?: { displayId: string; id: string }[];
  provinceError?: string;
  citySelectedValue?: { displayId: string; id: string } | null;
  onCityChange?: (value: string) => void;
  city: string;
  cityError?: string;
  caeIpiSelectedValue?: { displayId: string; id: string } | null;
  onCaeIpiChange?: (value: string) => void;
  caeIpi: string;
  caeIpiError?: string;
  representativesOnRestore: (id: string) => void;
  representativesOnCommitChanges: (changes: GridChangeSet) => void;
  representativesValidateChanges: (changes: GridChangeSet) => boolean;
  representativesRows: CrudRow[];
  publisherRepresentativeCreateStore: PublisherRepresentativeCreateStore;
  representativesError?: string;
  representativesVisibleColumns: GridColumn[];
  managementSocietiesOnRestore: (id: string) => void;
  managementSocietiesOnCommitChanges: (changes: GridChangeSet) => void;
  managementSocietiesValidateChanges: (changes: GridChangeSet) => boolean;
  managementSocietiesRows: CrudRow[];
  publisherCollectingSocietyCreateStore: PublisherCollectingSocietyCreateStore;
  managementSocietiesError?: string;
  managementSocietiesVisibleColumns: GridColumn[];
  dummyCRUDRestoredObservable: number;
  publisherCreateStore: PublisherCreateStore;
};

export const PublisherCreateForm: React.FC<PublisherCreateFormProps> = observer(
  ({
    onSave = () => {},
    onCancel = () => {},
    saving = false,
    showToolbox = true,
    showSaveFooter,
    personTypeError,
    personTypeOptions = [],
    personType = "" as PersonType,
    onPersonTypeChange = () => {},
    name = "",
    nameError,
    onNameChange = () => {},
    maritalStatusError,
    maritalStatusOptions = [],
    maritalStatus = "" as MaritalStatus | "",
    onMaritalStatusChange = () => {},
    onNationalitySelected = () => {},
    nationalitySelectedValue = null,
    nationalityOptions = [],
    nationalityError,
    onNationalityChange = () => {},
    pseudonym = "",
    pseudonymError,
    onPseudonymChange = () => {},
    taxId = "",
    taxIdError,
    onTaxIdChange = () => {},
    phone = "",
    phoneError,
    onPhoneChange = () => {},
    email = "",
    emailError,
    onEmailChange = () => {},
    streetAndNumber = "",
    streetAndNumberError,
    onStreetAndNumberChange = () => {},
    floor = "",
    floorError,
    onFloorChange = () => {},
    suite = "",
    suiteError,
    onSuiteChange = () => {},
    postalCode = "",
    postalCodeError,
    onPostalCodeChange = () => {},
    onCountrySelected = () => {},
    countrySelectedValue = null,
    countryOptions = [],
    countryError,
    onCountryChange = () => {},
    onProvinceSelected = () => {},
    provinceSelectedValue = null,
    provinceOptions = [],
    provinceError,
    onProvinceChange = () => {},
    city = "",
    cityError,
    onCityChange = () => {},
    caeIpi = "",
    caeIpiError,
    onCaeIpiChange = () => {},
    representativesOnRestore,
    representativesOnCommitChanges,
    representativesValidateChanges,
    representativesRows,
    publisherRepresentativeCreateStore,
    representativesError,
    representativesVisibleColumns,
    managementSocietiesOnRestore,
    managementSocietiesOnCommitChanges,
    managementSocietiesValidateChanges,
    managementSocietiesRows,
    publisherCollectingSocietyCreateStore,
    managementSocietiesError,
    managementSocietiesVisibleColumns,
    dummyCRUDRestoredObservable,
    publisherCreateStore,
  }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (publisherRepresentativeCreateStore.status === "uninitialized") {
      publisherRepresentativeCreateStore.initialize();
    }

    if (publisherCollectingSocietyCreateStore.status === "uninitialized") {
      publisherCollectingSocietyCreateStore.initialize();
    }

    return (
      <Grid>
        {showToolbox && (
          <FormToolbox
            saveAndCloseLabel={langStore.saveEntity}
            closeLabel={langStore.cancelNewEntity}
            saveForm={() => onSave()}
            saveDisabled={saving}
            closeForm={() => onCancel()}
          />
        )}
        <FirstPublisherBanner />
        <Box>
          <FormRecordProvider
            values={{
              personType,
              name,
              maritalStatus,
              nationality: nationalitySelectedValue,
              pseudonym,
              taxId,
              phone,
              email,
              streetAndNumber,
              floor,
              suite,
              postalCode,
              country: countrySelectedValue,
              province: provinceSelectedValue,
              city,
              caeIpi,
            }}
          >
            <FormExtras
              entity="Publisher"
              mode="create"
              values={{
                personType,
                name,
                maritalStatus,
                nationality: nationalitySelectedValue,
                pseudonym,
                taxId,
                phone,
                email,
                streetAndNumber,
                floor,
                suite,
                postalCode,
                country: countrySelectedValue,
                province: provinceSelectedValue,
                city,
                caeIpi,
              }}
              options={{
                nationality: nationalityOptions,
                country: countryOptions,
                province: provinceOptions,
              }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "personType":
                    onPersonTypeChange?.(valor);
                    break;
                  case "name":
                    onNameChange?.(valor);
                    break;
                  case "maritalStatus":
                    onMaritalStatusChange?.(valor);
                    break;
                  case "nationality":
                    onNationalitySelected?.(valor);
                    break;
                  case "pseudonym":
                    onPseudonymChange?.(valor);
                    break;
                  case "taxId":
                    onTaxIdChange?.(valor);
                    break;
                  case "phone":
                    onPhoneChange?.(valor);
                    break;
                  case "email":
                    onEmailChange?.(valor);
                    break;
                  case "streetAndNumber":
                    onStreetAndNumberChange?.(valor);
                    break;
                  case "floor":
                    onFloorChange?.(valor);
                    break;
                  case "suite":
                    onSuiteChange?.(valor);
                    break;
                  case "postalCode":
                    onPostalCodeChange?.(valor);
                    break;
                  case "country":
                    onCountrySelected?.(valor);
                    break;
                  case "province":
                    onProvinceSelected?.(valor);
                    break;
                  case "city":
                    onCityChange?.(valor);
                    break;
                  case "caeIpi":
                    onCaeIpiChange?.(valor);
                    break;
                }
              }}
            />

            <GroupContainer
              id="basicInformation"
              errorText={publisherCreateStore.getCardErrors("basicInformation")}
              title={langStore.groupPublisherBasicInformationLabel}
              collapsible={true}
              expanded={publisherCreateStore.getCardState("basicInformation")}
              toggleExpand={() =>
                publisherCreateStore.toggleCardState("basicInformation")
              }
            >
              <FormControl fullWidth>
                <InputReference
                  id="personType"
                  autoFocus
                  error={Boolean(personTypeError)}
                  helperText={
                    personTypeError ||
                    publisherCreateStore.warnings["personType"]?.message
                  }
                  label={langStore.PublisherPersonTypeLabel}
                  options={personTypeOptions}
                  selectedValue={
                    personType
                      ? {
                          id: personType,
                          displayId:
                            personTypeOptions.find(
                              (o: { id: string }) => o.id === personType
                            )?.displayId || personType,
                        }
                      : null
                  }
                  onChange={() => {}}
                  onSelect={(value: { id: string; displayId: string } | null) =>
                    onPersonTypeChange(
                      (value?.id as PersonType) || ("" as PersonType)
                    )
                  }
                />

                {personType === "naturalPerson" ? (
                  <>
                    <PersonaFisicaNameFields
                      value={name}
                      onChange={(value: string) => onNameChange(value)}
                      error={Boolean(nameError)}
                      helperText={nameError}
                    />
                    <InputReference
                      id="maritalStatus"
                      error={Boolean(maritalStatusError)}
                      helperText={
                        maritalStatusError ||
                        publisherCreateStore.warnings["maritalStatus"]?.message
                      }
                      label={langStore.PublisherMaritalStatusLabel}
                      options={maritalStatusOptions}
                      nullText={langStore.optionalFieldHint}
                      selectedValue={
                        maritalStatus
                          ? {
                              id: maritalStatus,
                              displayId:
                                maritalStatusOptions.find(
                                  (o: { id: string }) => o.id === maritalStatus
                                )?.displayId || maritalStatus,
                            }
                          : null
                      }
                      onChange={() => {}}
                      onSelect={(value: { id: string; displayId: string } | null) =>
                        onMaritalStatusChange(
                          (value?.id as MaritalStatus | "") ||
                            ("" as MaritalStatus | "")
                        )
                      }
                    />
                    <InputReference
                      id="nationality"
                      label={langStore.PublisherNationalityLabel}
                      error={Boolean(nationalityError)}
                      helperText={
                        nationalityError ||
                        publisherCreateStore.warnings["nationality"]?.message
                      }
                      selectedValue={nationalitySelectedValue}
                      onChange={(value: string) => onNationalityChange(value)}
                      onSelect={(value: { displayId: string; id: string } | null) =>
                        onNationalitySelected(value)
                      }
                      options={nationalityOptions}
                      nullText={langStore.optionalFieldHint}
                      description={langStore.PublisherNationalityDescription}
                      canCreate={rootStore.authStore.hasPermissions(
                        "country_create"
                      )}
                      renderCreateDialog={(dialogProps) => (
                        <CreateEntityDialog
                          {...dialogProps}
                          createStore={(rootStore) =>
                            new CountryCreateStore(rootStore)
                          }
                          renderForm={(store) => (
                            <CountryCreate
                              countryCreateStore={store}
                              showToolbox={false}
                              showSaveFooter={false}
                            />
                          )}
                          saveMethod="saveNewCountry"
                          cancelMethod="cancelNewCountry"
                        />
                      )}
                    />
                  </>
                ) : (
                  <InputString
                    value={name}
                    error={Boolean(nameError)}
                    warning={
                      !nameError && Boolean(publisherCreateStore.warnings["name"])
                    }
                    helperText={
                      nameError || publisherCreateStore.warnings["name"]?.message
                    }
                    label={langStore.PublisherNameLabel}
                    onChange={(value: string) => onNameChange(value)}
                    description={langStore.PublisherNameDescription}
                  />
                )}

                <InputString
                  value={pseudonym}
                  error={Boolean(pseudonymError)}
                  warning={
                    !pseudonymError &&
                    Boolean(publisherCreateStore.warnings["pseudonym"])
                  }
                  helperText={
                    pseudonymError ||
                    publisherCreateStore.warnings["pseudonym"]?.message
                  }
                  label={langStore.PublisherPseudonymLabel}
                  onChange={(value: string) => onPseudonymChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.PublisherPseudonymDescription}
                />
                <InputStringMask
                  value={taxId}
                  error={Boolean(taxIdError)}
                  warning={
                    !taxIdError &&
                    Boolean(publisherCreateStore.warnings["taxId"])
                  }
                  helperText={
                    taxIdError ||
                    publisherCreateStore.warnings["taxId"]?.message
                  }
                  label={langStore.PublisherTaxIdLabel}
                  onChange={(value: string) => onTaxIdChange(value)}
                  maskConfig={{ mask: "99-99999999-9" }}
                  description={langStore.PublisherTaxIdDescription}
                />

                <InputPhone
                  value={phone}
                  error={Boolean(phoneError)}
                  helperText={
                    phoneError ||
                    publisherCreateStore.warnings["phone"]?.message
                  }
                  label={langStore.PublisherPhoneLabel}
                  onChange={(value: string) => onPhoneChange(value)}
                  description={langStore.PublisherPhoneDescription}
                />

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <InputString
                    value={email}
                    error={Boolean(emailError)}
                    warning={
                      !emailError &&
                      Boolean(publisherCreateStore.warnings["email"])
                    }
                    helperText={
                      emailError ||
                      publisherCreateStore.warnings["email"]?.message
                    }
                    label={langStore.PublisherEmailLabel}
                    onChange={(value: string) => onEmailChange(value)}
                    description={langStore.PublisherEmailDescription}
                  />
                  <EmailVerificationIndicator
                    email={email}
                    entityName="Publisher"
                  />
                </Box>
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="address"
              errorText={publisherCreateStore.getCardErrors("address")}
              title={langStore.groupPublisherAddressLabel}
              collapsible={true}
              expanded={publisherCreateStore.getCardState("address")}
              toggleExpand={() =>
                publisherCreateStore.toggleCardState("address")
              }
            >
              <FormControl fullWidth>
                <InputString
                  value={streetAndNumber}
                  error={Boolean(streetAndNumberError)}
                  warning={
                    !streetAndNumberError &&
                    Boolean(publisherCreateStore.warnings["streetAndNumber"])
                  }
                  helperText={
                    streetAndNumberError ||
                    publisherCreateStore.warnings["streetAndNumber"]?.message
                  }
                  label={langStore.PublisherStreetAndNumberLabel}
                  onChange={(value: string) => onStreetAndNumberChange(value)}
                  description={langStore.PublisherStreetAndNumberDescription}
                />

                <InputString
                  value={floor}
                  error={Boolean(floorError)}
                  warning={
                    !floorError &&
                    Boolean(publisherCreateStore.warnings["floor"])
                  }
                  helperText={
                    floorError ||
                    publisherCreateStore.warnings["floor"]?.message
                  }
                  label={langStore.PublisherFloorLabel}
                  onChange={(value: string) => onFloorChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.PublisherFloorDescription}
                />

                <InputString
                  value={suite}
                  error={Boolean(suiteError)}
                  warning={
                    !suiteError &&
                    Boolean(publisherCreateStore.warnings["suite"])
                  }
                  helperText={
                    suiteError ||
                    publisherCreateStore.warnings["suite"]?.message
                  }
                  label={langStore.PublisherSuiteLabel}
                  onChange={(value: string) => onSuiteChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.PublisherSuiteDescription}
                />

                <InputString
                  value={postalCode}
                  error={Boolean(postalCodeError)}
                  warning={
                    !postalCodeError &&
                    Boolean(publisherCreateStore.warnings["postalCode"])
                  }
                  helperText={
                    postalCodeError ||
                    publisherCreateStore.warnings["postalCode"]?.message
                  }
                  label={langStore.PublisherPostalCodeLabel}
                  onChange={(value: string) => onPostalCodeChange(value)}
                  description={langStore.PublisherPostalCodeDescription}
                />
                <InputReference
                  id="country"
                  label={langStore.PublisherCountryLabel}
                  error={Boolean(countryError)}
                  helperText={
                    countryError ||
                    publisherCreateStore.warnings["country"]?.message
                  }
                  selectedValue={countrySelectedValue}
                  onChange={(value: string) => onCountryChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onCountrySelected(value)
                  }
                  options={countryOptions}
                  description={langStore.PublisherCountryDescription}
                  canCreate={rootStore.authStore.hasPermissions(
                    "country_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new CountryCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <CountryCreate
                          countryCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewCountry"
                      cancelMethod="cancelNewCountry"
                    />
                  )}
                />
                <InputReference
                  id="province"
                  label={langStore.PublisherProvinceLabel}
                  error={Boolean(provinceError)}
                  helperText={
                    provinceError ||
                    publisherCreateStore.warnings["province"]?.message
                  }
                  selectedValue={provinceSelectedValue}
                  onChange={(value: string) => onProvinceChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onProvinceSelected(value)
                  }
                  options={provinceOptions}
                  description={langStore.PublisherProvinceDescription}
                  canCreate={rootStore.authStore.hasPermissions(
                    "provinceState_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new ProvinceStateCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <ProvinceStateCreate
                          provinceStateCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewProvinceState"
                      cancelMethod="cancelNewProvinceState"
                    />
                  )}
                />

                <InputString
                  value={city}
                  error={Boolean(cityError)}
                  warning={
                    !cityError && Boolean(publisherCreateStore.warnings["city"])
                  }
                  helperText={
                    cityError || publisherCreateStore.warnings["city"]?.message
                  }
                  label={langStore.PublisherCityLabel}
                  onChange={(value: string) => onCityChange(value)}
                  description={langStore.PublisherCityDescription}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="legalInformation"
              errorText={publisherCreateStore.getCardErrors("legalInformation")}
              title={langStore.groupPublisherLegalInformationLabel}
              collapsible={true}
              expanded={publisherCreateStore.getCardState("legalInformation")}
              toggleExpand={() =>
                publisherCreateStore.toggleCardState("legalInformation")
              }
            >
              <FormControl fullWidth>
                <PublisherCaeIpiCustomField
                  value={caeIpi}
                  error={Boolean(caeIpiError)}
                  helperText={
                    caeIpiError ||
                    publisherCreateStore.warnings["caeIpi"]?.message
                  }
                  {...{ optional: false }}
                  label={langStore.PublisherCaeIpiLabel}
                  description={langStore.PublisherCaeIpiDescription}
                  onChange={(value: string) => onCaeIpiChange(value)}
                />
                <ItemsCRUDCompact
                  label={langStore.PublisherRepresentativesLabel}
                  description={langStore.PublisherRepresentativesDescription}
                  showLabel={true}
                  rows={representativesRows}
                  columns={representativesVisibleColumns}
                  popupComponent={(row: any) => {
                    if (
                      row &&
                      row.id &&
                      !(
                        typeof row.id === "string" && row.id.startsWith("auto-")
                      )
                    ) {
                      runInAction(() =>
                        publisherCreateStore.representativesLoadRow(row)
                      );
                    }
                    return (
                      <PublisherRepresentativeCreate
                        publisherRepresentativeCreateStore={
                          publisherRepresentativeCreateStore
                        }
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    );
                  }}
                  popupMaxWidth="md"
                  onRestore={(id: string) => representativesOnRestore(id)}
                  onCommitChanges={(changes: GridChangeSet) =>
                    representativesOnCommitChanges(changes)
                  }
                  validate={(changes: GridChangeSet) =>
                    representativesValidateChanges(changes)
                  }
                  dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                  error={representativesError}
                />
                <ItemsCRUDCompact
                  label={langStore.PublisherManagementSocietiesLabel}
                  description={
                    langStore.PublisherManagementSocietiesDescription
                  }
                  showLabel={true}
                  rows={managementSocietiesRows}
                  columns={managementSocietiesVisibleColumns}
                  popupComponent={(row: any) => {
                    if (
                      row &&
                      row.id &&
                      !(
                        typeof row.id === "string" && row.id.startsWith("auto-")
                      )
                    ) {
                      runInAction(() =>
                        publisherCreateStore.managementSocietiesLoadRow(row)
                      );
                    }
                    return (
                      <PublisherCollectingSocietyCreate
                        publisherCollectingSocietyCreateStore={
                          publisherCollectingSocietyCreateStore
                        }
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    );
                  }}
                  popupMaxWidth="sm"
                  onRestore={(id: string) => managementSocietiesOnRestore(id)}
                  onCommitChanges={(changes: GridChangeSet) =>
                    managementSocietiesOnCommitChanges(changes)
                  }
                  validate={(changes: GridChangeSet) =>
                    managementSocietiesValidateChanges(changes)
                  }
                  dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                  error={managementSocietiesError}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="systemDates"
              errorText={publisherCreateStore.getCardErrors("systemDates")}
              title={langStore.groupPublisherSystemDatesLabel}
              collapsible={true}
              expanded={publisherCreateStore.getCardState("systemDates")}
              toggleExpand={() =>
                publisherCreateStore.toggleCardState("systemDates")
              }
            >
              <FormControl fullWidth></FormControl>
            </GroupContainer>
          </FormRecordProvider>
        </Box>
        {(showSaveFooter ?? !showToolbox) && (
          <FormSaveFooter
            onSave={() => onSave()}
            label={langStore.saveEntity}
            disabled={saving}
          />
        )}
      </Grid>
    );
  }
);
